import React from 'react';
import { Link } from 'react-router-dom';

import { DragDropContext, Droppable, Draggable, type DropResult } from '@hello-pangea/dnd';
import { Button } from '../../../components/ui/button';
import { Checkbox } from '../../../components/ui/checkbox';
import { ExternalLink, GripVertical, Trash2 } from 'lucide-react';
import { cn } from 'lib/utils';
import { formatQuestionTypeSlug, plainQuestionLabel } from '../QuestionLibrary/navigatorUtils';
import type { FlowQuestion } from './types';

import { type ContentCourseRef, questionLibraryHref } from '../contentToolLinks';

export type FlowQuestionListProps = {
  questions: FlowQuestion[];
  onDragEnd: (result: DropResult) => void;
  onRemoveQuestion: (id: number) => void;
  onToggleRequired: (id: number, checked: boolean | 'indeterminate') => void;
  /** Where "Edit in Question Library" opens: this module in this course, at the question's step. */
  courseId?: ContentCourseRef | null;
  flowId?: number | null;
};

export const FlowQuestionList: React.FC<FlowQuestionListProps> = ({
  questions,
  onDragEnd,
  onRemoveQuestion,
  onToggleRequired,
  courseId,
  flowId,
}) => {
  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId="flow-questions">
        {(provided) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className="space-y-2 rounded-lg border border-border/40 bg-white/5 p-3 transition-colors"
          >
            {questions.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">
                No questions added yet. Use the field above to add the first question.
              </p>
            ) : (
              questions.map((question, index) => (
                <Draggable key={question.question_id} draggableId={String(question.question_id)} index={index}>
                  {(draggableProvided, draggableSnapshot) => {
                    const draggableStyle = {
                      ...(draggableProvided.draggableProps.style ?? {}),
                    };
                    const label = plainQuestionLabel(question.text);

                    return (
                      <div
                        ref={draggableProvided.innerRef}
                        {...draggableProvided.draggableProps}
                        style={draggableStyle}
                        className={cn(
                          "flex min-w-0 flex-wrap items-start gap-3 rounded-md border bg-white/90 p-3 text-sm transition-shadow",
                          draggableSnapshot.isDragging && "shadow-lg ring-2 ring-primary/50",
                        )}
                        data-testid={`fb-question-row-${question.question_id}`}
                      >
                        <button
                          type="button"
                          className="mt-0.5 shrink-0 rounded-md border border-white/20 bg-transparent p-1 text-muted-foreground transition hover:bg-white/10"
                          {...draggableProvided.dragHandleProps}
                          aria-label="Drag to reorder"
                        >
                          <GripVertical className="h-4 w-4" />
                        </button>
                        <span className="mt-1 w-5 shrink-0 text-right text-xs text-muted-foreground">{index + 1}</span>
                        <div className="flex min-w-0 flex-1 flex-col gap-1 break-words">
                          <div className="font-medium text-foreground">
                            {label || `Question ${question.question_id}`}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {question.question_type ? `${formatQuestionTypeSlug(question.question_type)} · ` : ''}
                            ID: {question.question_id}
                            {question.question_key ? ` - ${question.question_key}` : ""}
                          </div>
                        </div>
                        <div className="flex w-full shrink-0 items-center justify-end gap-2 sm:w-auto">
                          <label className="flex items-center gap-2 text-xs text-muted-foreground">
                            <Checkbox
                              checked={question.is_required}
                              onCheckedChange={(checked) => onToggleRequired(question.question_id, checked)}
                              aria-label={`Mark question ${question.question_id} as required`}
                            />
                            Required
                          </label>
                          <Link
                            to={questionLibraryHref({ courseId, flowId, questionId: question.question_id })}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground transition hover:bg-black/5 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            title="Edit in Question Library"
                            aria-label={`Edit question ${question.question_id} in Question Library`}
                          >
                            <ExternalLink className="h-4 w-4" aria-hidden="true" />
                          </Link>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => onRemoveQuestion(question.question_id)}
                            aria-label={`Remove question ${question.question_id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  }}
                </Draggable>
              ))
            )}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
};
